import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Spin, Button, Empty, Alert } from 'antd';
import { Trans, useTranslation } from 'react-i18next';
import { ArrowLeftOutlined, PrinterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import axiosInstance from '../../variables/api/axiosInstance';
import logoCarteBrune from '../../assets/images/logo.png';
import { IndicesReedition, markPrinted, wasPrinted } from './indiceReedition';
import { useReglagesIndices } from './useReglagesIndices';
import './attestations.css';
import BlocSignature from '../../components/BlocSignature';
import { useAvertissementSignature } from '../../components/avertissementSignature';
import { dateDocument, tDocument, useLangueDocument } from '../../i18n/documents';

/* ─────────────────────────────────────────────────────────────
   ATTESTATION DE NON GARANTIE - BNCB
   Émis quand l'assureur étranger n'a pas confirmé la garantie.
   Route : /sinistres/:id/non-garantie
   ───────────────────────────────────────────────────────────── */
export default function AttestationNonGarantie() {

  /* Les signatures sont temporaires : celle qui valait a l'ouverture de cet
     ecran peut avoir expire. L'etat est relu au clic, et l'agent est averti que
     la feuille sortira vierge - puis libre a lui d'imprimer. */
  const { confirmerPuisImprimer } = useAvertissementSignature('ATTESTATION_NON_GARANTIE');
  const { id } = useParams();
  // Écran dans la langue de l'agent ; attestation dans celle du destinataire.
  const { t } = useTranslation('documents');
  const reglagesIndices = useReglagesIndices();
  const navigate = useNavigate();
  const [sinistre, setSinistre] = useState(null);
  const [assure, setAssure] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const sRes = await axiosInstance.get(`/v1/sinistres/${id}`);
      const s = sRes.data?.data;
      setSinistre(s);
      if (s?.assureTrackingId) {
        try {
          const aRes = await axiosInstance.get(`/v1/assures/${s.assureTrackingId}`);
          setAssure(aRes.data?.data);
        } catch { /* ignore */ }
      }
    } catch {
      setSinistre(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // TE : l'attestation part au bureau étranger, dans sa langue. ET : imprimée sur
  // papier à en-tête et remise au Togo, en français.
  const estTe = sinistre?.typeSinistre === 'SURVENU_ETRANGER';
  const { langue, pret } = useLangueDocument(estTe ? sinistre?.organismeHomologueTrackingId : null);
  const tDoc = tDocument(langue);

  if (loading) return <div className="att-page"><Spin /></div>;
  if (!sinistre) {
    return (
      <div className="att-page">
        <Empty description={t('ecran.sinistreIntrouvable')} />
        <Button onClick={() => navigate('/sinistres')} icon={<ArrowLeftOutlined />} style={{ marginTop: 12 }}>
          {t('ecran.retour')}
        </Button>
      </div>
    );
  }

  const fmtDateLong = (d) => d ? dateDocument(d, 'DD MMMM YYYY', langue) : '-';
  const fmtDateUpper = (d) => d ? dateDocument(d, 'DD MMMM YYYY', langue).toUpperCase() : '-';

  const assureur = sinistre.organismeMembreRaisonSociale || tDoc('nonGarantie.assureurParDefaut');
  const paysEmetteur = sinistre.paysEmetteurLibelle || '-';
  const nomAssure = assure?.nomComplet || sinistre.assureNomComplet || '-';
  const immat = assure?.immatriculation || sinistre.assureImmatriculation || '-';
  const dateAccident = fmtDateLong(sinistre.dateAccident);
  const lieuAccident = sinistre.ville || sinistre.lieuAccident || '-';
  const numeroSinistre = sinistre.numeroSinistreLocal
    ? sinistre.numeroSinistreLocal.replace(/\s*\/\s*/g, '/') + '/NG'
    : '-';

  // Type dommage pour le texte narratif
  const domLibelle = sinistre.typeDommage === 'MIXTE'
    ? tDoc('nonGarantie.dommage.mixte')
    : sinistre.typeDommage === 'CORPOREL'
      ? tDoc('nonGarantie.dommage.corporel')
      : tDoc('nonGarantie.dommage.materiel');

  const courrierRef = sinistre.courrierNonGarantieRef
    || tDoc('nonGarantie.copieCourrier', { pays: paysEmetteur.toUpperCase() });
  const courrierDate = sinistre.courrierNonGarantieDate || sinistre.dateAccident;

  const dateFaite = sinistre.dateConfirmationGarantie || dayjs().format('YYYY-MM-DD');
  const dejaImprime = wasPrinted('non-garantie', id);
  // TE = sinistre survenu à l'étranger (assuré togolais) : l'attestation part vers un
  // bureau étranger, elle doit donc porter l'en-tête complet du Bureau National.
  const estSinistreTogolaisEtranger = estTe;

  return (
    <div className="att-page">
      <div className="att-toolbar no-print">
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/sinistres/${id}`)}>
          {t('ecran.retourDossier')}
        </Button>
        <Button type="primary" icon={<PrinterOutlined />} disabled={!pret} onClick={() => confirmerPuisImprimer(() => { markPrinted('non-garantie', id); window.print(); })} style={{ background: '#1a6b3c', borderColor: '#1a6b3c' }}>
          {t('ecran.imprimer')}
        </Button>
      </div>
      {langue === 'en' && (
        <Alert type="info" showIcon className="no-print" style={{ marginBottom: 12, borderRadius: 8 }}
          message={t('ecran.lettreEnAnglais')} />
      )}

      <div className="att-sheet">
        <div style={{ position: 'absolute', top: '8mm', right: '12mm', zIndex: 2 }}>
          <IndicesReedition
            dejaModifie={!!sinistre?.dejaModifie}
            dejaImprime={dejaImprime}
            reglages={reglagesIndices}
          />
        </div>
        {/* ═══════ Entête bilingue - sinistres togolais à l'étranger (TE) uniquement.
             Les sinistres survenus au Togo (ET) s'impriment sur papier à en-tête. ═══════ */}
        {estSinistreTogolaisEtranger && (
          <div className="att-header">
            <div className="att-header-left">
              BUREAU NATIONAL TOGOLAIS<br />
              SYSTÈME CARTE BRUNE CEDEAO
            </div>
            <img src={logoCarteBrune} alt="Carte Brune CEDEAO" className="att-logo-img" />
            <div className="att-header-right">
              TOGO NATIONAL BUREAU<br />
              ECOWAS BROWN CARD
            </div>
          </div>
        )}

        {/* ═══════ Titre centré - sans en-tête, on réserve l'espace du papier à en-tête ═══════ */}
        <div style={{ textAlign: 'center', marginTop: estSinistreTogolaisEtranger ? '10mm' : '24mm', marginBottom: '22mm' }}>
          <h1 className="att-title" style={{ textDecoration: 'underline', fontSize: '18pt' }}>
            {tDoc('nonGarantie.titre')}
          </h1>
        </div>

        {/* ═══════ Date et N° sinistre ═══════ */}
        <div style={{ marginBottom: '14mm', fontSize: '12pt', lineHeight: 1.8 }}>
          <div>
            <strong><u>{tDoc('nonGarantie.dateAccident')}</u></strong>{tDoc('commun.deuxPoints')} {dateAccident}
          </div>
          <div>
            <strong>{tDoc('nonGarantie.notreSinistre')}</strong>{tDoc('commun.deuxPoints')} {numeroSinistre}
          </div>
        </div>

        {/* ═══════ Texte narratif ═══════ */}
        <div className="att-narratif">
          <Trans
            t={tDoc}
            i18nKey="nonGarantie.narratif"
            values={{
              assureur: assureur.toUpperCase(),
              pays: paysEmetteur.toUpperCase(),
              assure: nomAssure.toUpperCase(),
              immat: immat.toUpperCase(),
              dommage: domLibelle,
              date: dateAccident,
              lieu: lieuAccident,
            }}
            components={{ s: <strong className="underline" />, u: <span style={{ textDecoration: 'underline' }} /> }}
          />
        </div>

        {/* ═══════ Pièces jointes ═══════ */}
        <div style={{ marginTop: '10mm', fontSize: '11pt' }}>
          <div className="pj-label">{tDoc('nonGarantie.pj')}</div>
          <div style={{ marginTop: 4 }}>
            - {courrierRef}
            {courrierDate && <> {tDoc('nonGarantie.enDateDu')} <strong>{fmtDateLong(courrierDate)}</strong></>}
          </div>
        </div>

        {/* ═══════ Fait à Lomé ═══════ */}
        <div style={{ marginTop: '28mm', textAlign: 'right', fontSize: '11.5pt' }}>
          {tDoc('commun.faitALome')} <strong>{fmtDateUpper(dateFaite)}</strong>
        </div>
        <div className="att-pour-bncb">{tDoc('commun.pourBureauNational')}</div>
        <div className="att-signature-espace">
          <BlocSignature
            codeDocument="ATTESTATION_NON_GARANTIE"
            sinistreTrackingId={sinistre?.sinistreTrackingId}
          />
        </div>

        {/* ═══════ Footer ═══════ */}
        <div className="att-footer">
          {tDoc('commun.piedAdresse')}<br />
          {tDoc('commun.piedCourriel')}
        </div>
      </div>
    </div>
  );
}
